import { Component } from '@theme/component';

/**
 * Shopify Forms renders the form inside an open shadow root, with 5px corners on the
 * fields and the button. The brand uses square corners everywhere, so this sheet is
 * added to that shadow root and zeroes the app's radius variables. The fields draw
 * their border as a 1px outer shadow, which makes them look 2px wider than the button;
 * it is drawn inside instead, so the fields, the file drop zone and the button all share
 * one width. The fields also follow the block's text colour (the app gives them their
 * own #202020). These are the app's internals: if a later app version
 * changes them, the form simply falls back to its own look.
 */
const formOverrides = new CSSStyleSheet();
formOverrides.replaceSync(`
  :host,
  :host * {
    --form-field-border-radius: 0px !important;
    --button-border-radius: 0px !important;
    --buttons-radius: 0px !important;
    --container-border-radius: 0px !important;
    --form-text-color: var(--body-text-color) !important;
    --form-field-border: inset 0 0 0 1px color-mix(in srgb, var(--form-text-color) 20%, transparent) !important;
  }

  /* The textarea's label backdrop would cover the inner border at the top and left. */
  div:has(> textarea)::before {
    top: 1px !important;
    left: 1px !important;
  }

  /* The file drop zone: a plain grey area as in the design, instead of a dashed frame. */
  div:has(> label > input[type='file']) {
    box-sizing: border-box;
    min-height: 120px;
    border: 0 !important;
    background-color: color-mix(in srgb, var(--form-text-color) 10%, transparent);
  }

  div:has(> label > input[type='file']) > div > span {
    background-color: transparent;
  }

  @media (min-width: 990px) {
    div:has(> label > input[type='file']) {
      min-height: 220px;
    }
  }

  /* The app sizes the form to its content; fill the column up to the app's 450px.
     cqi is measured on the host, which the section CSS makes a container. */
  section {
    width: min(450px, 100cqi);
  }
`);

/**
 * Custom jewellery request section: fits the Shopify Forms form to the brand and the column.
 * The slider is the stock slideshow-component and needs nothing here.
 */
class CustomJewelleryRequest extends Component {
  /** @type {MutationObserver[]} */
  #observers = [];

  connectedCallback() {
    super.connectedCallback();
    this.#watch();
    customElements.whenDefined('shopify-forms-embed').then(() => {
      if (this.isConnected) this.#styleForms();
    });
  }

  updatedCallback() {
    super.updatedCallback();
    this.#styleForms();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopWatching();
  }

  /** The form appears later in the theme editor and after the app's own re-renders. */
  #watch() {
    const observer = new MutationObserver(() => this.#styleForms());
    observer.observe(this, { childList: true, subtree: true });
    this.#observers.push(observer);
    this.#styleForms();
  }

  #stopWatching() {
    for (const observer of this.#observers) observer.disconnect();
    this.#observers = [];
  }

  #styleForms() {
    for (const embed of this.querySelectorAll('shopify-forms-embed')) {
      const root = embed.shadowRoot;
      if (!root || root.adoptedStyleSheets.includes(formOverrides)) continue;

      root.adoptedStyleSheets = [...root.adoptedStyleSheets, formOverrides];

      // The app may replace its stylesheets when it re-renders (e.g. after submitting).
      const observer = new MutationObserver(() => {
        if (!root.adoptedStyleSheets.includes(formOverrides)) {
          root.adoptedStyleSheets = [...root.adoptedStyleSheets, formOverrides];
        }
      });
      observer.observe(root, { childList: true, subtree: true });
      this.#observers.push(observer);
    }
  }
}

if (!customElements.get('custom-jewellery-request')) {
  customElements.define('custom-jewellery-request', CustomJewelleryRequest);
}
